import React, { useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  ArrowLeft, Package, Loader2, RotateCcw, ExternalLink, 
  CheckCircle2, XCircle, AlertTriangle, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GRView = () => {
  const { material_document, fiscal_year } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reversing, setReversing] = useState(false);
  const [gr, setGr] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);


  const loadGR = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/goods-receipts/${material_document}/${fiscal_year}`);
      setGr(response.data);
    } catch (error) {
      console.error('Error loading GR:', error);
      toast.error(error.response?.data?.detail || 'Failed to load Goods Receipt');
      navigate('/p2p/gr-list');
    } finally {
      setLoading(false);
    }
  }, [material_document, fiscal_year, navigate]);

  useEffect(() => {
    loadGR();
  }, [loadGR]);

  const handleReverse = async () => {
    if (!window.confirm('Are you sure you want to reverse this Goods Receipt? This will reduce PO received quantities.')) {
      return;
    }
    
    try {
      setReversing(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/goods-receipts/${material_document}/${fiscal_year}/reverse?reversed_by=ADMIN`
      );
      toast.success(`GR reversed successfully. Reversal document: ${response.data.material_document}`);
      loadGR();
    } catch (error) {
      console.error('Error reversing GR:', error);
      toast.error(error.response?.data?.detail || 'Failed to reverse Goods Receipt');
    } finally {
      setReversing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading Goods Receipt...</span>
        </div>
      </Layout>
    );
  }

  if (!gr) return null;

  const currentItem = gr.items?.[activeItemIndex];
  const isReversed = gr.status === 'REVERSED';
  const isReversal = gr.movement_type === '102';
  
  // Only movement types 101 and 102 are eligible for reversal
  const eligibleForReversal = ['101', '102'].includes(gr.movement_type);
  const canReverse = !isReversed && !isReversal && eligibleForReversal;

  // Calculate totals
  const totalQuantity = gr.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
  const totalAmount = gr.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/gr-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">View Goods Receipt</h1>
              <p className="text-sm text-gray-500">
                Document: {gr.material_document} / {gr.material_document_year} | Movement Type: {gr.movement_type}
                {isReversal && ' (Reversal)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canReverse && (
              <Button variant="outline" onClick={handleReverse} disabled={reversing}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {reversing ? 'Reversing...' : 'Reverse GR'}
              </Button>
            )}
            {!eligibleForReversal && !isReversed && !isReversal && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Movement type {gr.movement_type} not eligible for reversal
              </span>
            )}
            <Button onClick={() => navigate(`/p2p/po-view/${gr.po_number}`)}>
              <ExternalLink className="w-4 h-4 mr-2" />View PO
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        <div className={`rounded-lg p-4 mb-6 flex items-center gap-3 ${
          isReversed ? 'bg-red-50 border border-red-200' : 
          isReversal ? 'bg-yellow-50 border border-yellow-200' :
          'bg-green-50 border border-green-200'
        }`}>
          {isReversed ? (
            <>
              <XCircle className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-medium text-red-800">This Goods Receipt has been REVERSED</p>
                <p className="text-sm text-red-700">Reversed by document: {gr.reversed_by}</p>
              </div>
            </>
          ) : isReversal ? (
            <>
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">This is a REVERSAL document</p>
                <p className="text-sm text-yellow-700">Reversal of: {gr.reversal_of}</p>
              </div>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Goods Receipt POSTED successfully</p>
                <p className="text-sm text-green-700">Posted on: {gr.posting_date} by {gr.created_by}</p>
              </div>
            </>
          )}
        </div>

        {/* PO Reference Section - Same as Create */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Reference</h2>
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-md">
              <Label>PO Number</Label>
              <Input value={gr.po_number} disabled className="bg-gray-100" />
            </div>
            <Button variant="outline" onClick={() => navigate(`/p2p/po-view/${gr.po_number}`)}>
              <FileText className="w-4 h-4 mr-2" />View PO
            </Button>
          </div>
        </div>

        {/* Organizational Data from PO - Same as Create */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <Package className="inline w-5 h-5 mr-2" />
            PO: {gr.po_number} - {gr.vendor_name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-gray-500">Vendor</Label>
              <p className="font-medium">{gr.vendor_id} - {gr.vendor_name}</p>
            </div>
            <div>
              <Label className="text-gray-500">Company Code</Label>
              <p className="font-medium">{gr.company_code} - {gr.company_code_name}</p>
            </div>
            <div>
              <Label className="text-gray-500">Purchasing Org</Label>
              <p className="font-medium">{gr.purchasing_org}</p>
            </div>
            <div>
              <Label className="text-gray-500">Currency</Label>
              <p className="font-medium">{gr.currency}</p>
            </div>
          </div>
          
          {/* Status Badges */}
          <div className="mt-4 flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isReversed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
            }`}>
              GR Status: {gr.status}
            </span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              {gr.items?.length || 0} item(s) received
            </span>
          </div>
        </div>

        {/* GR Header Fields - Same as Create but disabled */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">GR Header Data</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>GR Document Number</Label>
              <Input value={gr.material_document} disabled className="bg-gray-100 font-medium" />
            </div>
            <div>
              <Label>Fiscal Year</Label>
              <Input value={gr.material_document_year} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Posting Date</Label>
              <Input type="date" value={gr.posting_date} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Document Date</Label>
              <Input type="date" value={gr.document_date} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Delivery Note</Label>
              <Input value={gr.delivery_note || '-'} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Bill of Lading</Label>
              <Input value={gr.bill_of_lading || '-'} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Created By</Label>
              <Input value={gr.created_by} disabled className="bg-gray-100" />
            </div>
            <div>
              <Label>Created At</Label>
              <Input value={new Date(gr.created_at).toLocaleString()} disabled className="bg-gray-100" />
            </div>
            <div className="md:col-span-4">
              <Label>Header Text</Label>
              <Input value={gr.header_text || '-'} disabled className="bg-gray-100" />
            </div>
          </div>
        </div>

        {/* GR Items Section - Same as Create but disabled */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">GR Line Items</h2>
            <div className="text-sm text-gray-500">
              {gr.items?.length || 0} item(s) in this GR
            </div>
          </div>

          {/* Item Selector Buttons */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {gr.items?.map((item, index) => (
              <Button
                key={index}
                type="button"
                variant={activeItemIndex === index ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveItemIndex(index)}
              >
                Item {item.item_number}
                <CheckCircle2 className="w-3 h-3 ml-1 text-green-500" />
              </Button>
            ))}
          </div>

          {/* Current Item Details */}
          {currentItem && (
            <div className="border-t pt-4">
              {/* Material Info */}
              <div className="bg-orange-50 p-3 rounded-md mb-4">
                <p className="font-medium text-orange-900">
                  {currentItem.material_id} - {currentItem.material_description}
                </p>
                <p className="text-sm text-orange-700">
                  PO Item: {currentItem.po_item} | Plant: {currentItem.plant} | UOM: {currentItem.unit_of_measure}
                </p>
              </div>

              {/* Quantities Grid - Same as Create */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <Label className="text-gray-500">PO Quantity</Label>
                  <Input value={currentItem.po_quantity} disabled className="bg-gray-100 font-medium" />
                </div>
                <div>
                  <Label className="text-gray-500">Already Received</Label>
                  <Input value={currentItem.quantity_already_received} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label className="text-gray-500">Open Quantity</Label>
                  <Input 
                    value={currentItem.open_quantity} 
                    disabled 
                    className="bg-gray-100"
                  />
                </div>
                <div>
                  <Label className="text-green-700 font-semibold">Receipt Qty</Label>
                  <Input
                    value={currentItem.quantity}
                    disabled
                    className={`font-medium ${currentItem.quantity >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                  />
                </div>
                <div>
                  <Label className="text-gray-500">UOM</Label>
                  <Input value={currentItem.unit_of_measure} disabled className="bg-gray-100" />
                </div>
              </div>

              {/* Location & Price - Same as Create */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label>Storage Location</Label>
                  <Input value={currentItem.storage_location} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label>Stock Type</Label>
                  <Input 
                    value={currentItem.stock_type === 'Q' ? 'Quality Inspection' : 
                           currentItem.stock_type === 'S' ? 'Blocked' : 'Unrestricted'} 
                    disabled 
                    className="bg-gray-100" 
                  />
                </div>
                <div>
                  <Label>Batch Number</Label>
                  <Input value={currentItem.batch_number || '-'} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label className="text-gray-500">Net Price</Label>
                  <Input 
                    value={`${currentItem.net_price} / ${currentItem.price_unit} ${currentItem.unit_of_measure}`} 
                    disabled 
                    className="bg-gray-100" 
                  />
                </div>
              </div>

              {/* Item Value */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="md:col-span-2"></div>
                <div>
                  <Label className="text-gray-500">Currency</Label>
                  <Input value={currentItem.currency} disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label className="text-orange-700 font-semibold">Item Amount</Label>
                  <Input
                    value={`${currentItem.currency} ${currentItem.amount?.toFixed(2) || '0.00'}`}
                    disabled
                    className="bg-orange-50 font-medium text-right text-orange-700"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Totals Section - Same as Create */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">GR Totals</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <Label className="text-orange-700">Total Items</Label>
              <p className="text-2xl font-bold text-orange-900">{gr.items?.length || 0}</p>
            </div>
            <div className={`p-4 rounded-lg ${totalQuantity >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
              <Label className={totalQuantity >= 0 ? 'text-green-700' : 'text-red-700'}>Total Quantity</Label>
              <p className={`text-2xl font-bold ${totalQuantity >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {totalQuantity.toFixed(3)}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${totalAmount >= 0 ? 'bg-orange-50' : 'bg-red-50'}`}>
              <Label className={totalAmount >= 0 ? 'text-orange-700' : 'text-red-700'}>Total Value</Label>
              <p className={`text-2xl font-bold ${totalAmount >= 0 ? 'text-orange-900' : 'text-red-900'}`}>
                {gr.currency} {totalAmount.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/p2p/gr-list')}>
            Back to List
          </Button>
          {canReverse && (
            <Button variant="outline" onClick={handleReverse} disabled={reversing}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {reversing ? 'Reversing...' : 'Reverse GR'}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GRView;
